import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Box, Button, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import {
  CheckRounded as CheckIcon,
  DescriptionOutlined as PaperIcon,
  PhoneIphoneOutlined as PhoneIcon,
  ShoppingCartOutlined as CartIcon,
} from "@mui/icons-material";
import api from "../../../api";
import GettingStartedCard from "../../../components/GettingStartedCard";
import { toast } from "react-toastify";
import { useConfirmPurchase } from "./ConfirmPurchaseDialog";
import { completePanelPurchase, PANEL_PURCHASE_MESSAGES } from "../../../utils/panelPurchase";

const PURPLE = "#5B3FD6";
const LIST_LIMIT = 5;

const cardSx = {
  bgcolor: "#fff",
  border: "1px solid #E5E7F0",
  borderRadius: "20px",
  p: { xs: 2.5, md: 3.5 },
};
const primaryBtnSx = {
  textTransform: "none",
  fontWeight: 700,
  borderRadius: "12px",
  minHeight: 46,
  px: 3,
  bgcolor: PURPLE,
  boxShadow: "none",
  "&:hover": { bgcolor: "#4527B8", boxShadow: "none" },
};
const outlineBtnSx = {
  textTransform: "none",
  fontWeight: 700,
  borderRadius: "10px",
  minHeight: 44,
  borderWidth: "1.5px",
  "&:hover": { borderWidth: "1.5px" },
};

const STATUS_CHIP = {
  "Not started": { bg: "#F3F4F6", fg: "#374151" },
  "In progress": { bg: "#FEF3C7", fg: "#92400E" },
  Completed: { bg: "#DCFCE7", fg: "#14532D" },
};

const statusOf = (row) => (row.completed_at ? "Completed" : row.started_at ? "In progress" : "Not started");

/**
 * Mock exams and papers the student owns, newest first, in one shape. Mock exams open from the
 * Mock Exams page (a take needs a session started there first); papers open directly.
 */
function toItems(mockRows, paperRows, prefix) {
  const mocks = (mockRows || []).map((row) => {
    const status = statusOf(row);
    return {
      key: `m-${row.purchase_id}`,
      kind: "Mock exam",
      name: row.exam_name,
      status,
      at: row.purchased_at,
      to:
        status === "Completed"
          ? `/${prefix}/mock-exam/exam/${row.mock_exam_id}/report`
          : `/${prefix}/mock-exams?tab=exam`,
      action: status === "Completed" ? "See results" : status === "In progress" ? "Continue" : "Start",
    };
  });
  const papers = (paperRows || [])
    .filter((row) => row.type === "paper" && row.purchase_id)
    .map((row) => {
      const status = statusOf(row);
      return {
        key: `p-${row.purchase_id}`,
        kind: "Practice paper",
        name: row.paper_name,
        status,
        at: row.purchased_at,
        to:
          status === "Completed"
            ? `/${prefix}/past-paper/${row.purchase_id}/result`
            : `/${prefix}/past-paper/${row.purchase_id}/take`,
        action: status === "Completed" ? "See results" : status === "In progress" ? "Continue" : "Start",
      };
    });
  return [...mocks, ...papers].sort((a, b) => String(b.at || "").localeCompare(String(a.at || "")));
}

function SectionHeading({ children }) {
  return (
    <Typography variant="h6" sx={{ fontWeight: 700, color: "#111827", fontSize: { xs: "1.1rem", md: "1.25rem" } }}>
      {children}
    </Typography>
  );
}

/** The two packs plus links to single papers, as in DashboardShopCard. */
function PackRows({ packs, buyingId, onBuy }) {
  if (!packs.length) return null;
  return (
    <Box sx={{ border: "1px solid #EEF0F6", borderRadius: "14px" }}>
      {packs.map((pack, i) => (
        <Stack
          key={pack.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{ px: 2, py: 1.5, borderTop: i ? "1px solid #EEF0F6" : "none" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>{pack.name}</Typography>
            <Typography variant="body2" sx={{ color: "#4B5563" }}>
              All {pack.item_count} · {pack.price_display}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            disabled={buyingId === pack.id}
            onClick={() => onBuy(pack)}
            sx={{ ...primaryBtnSx, minHeight: 40, px: 2, borderRadius: "8px" }}
          >
            {buyingId === pack.id ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : "Buy"}
          </Button>
        </Stack>
      ))}
    </Box>
  );
}

function SingleLinks({ prefix }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
      <Button component={RouterLink} to={`/${prefix}/mock-exams?tab=exam`} variant="outlined" sx={outlineBtnSx}>
        Mock exams
      </Button>
      <Button component={RouterLink} to={`/${prefix}/past-papers`} variant="outlined" sx={outlineBtnSx}>
        Practice papers
      </Button>
    </Box>
  );
}

/**
 * Dashboard for a website student with no course and no running plan or trial.
 *
 * The course dashboard used to show here: course tiles, assignments and charts that are always
 * empty without a course, and nothing about what to do next. This one asks the question the
 * public site's My Learning asks — a plan, or single papers — and lists anything already bought.
 * Once something is bought, the list leads and the plan offer shrinks to a banner.
 */
export default function NoPlanDashboard({ studentPrefix }) {
  const navigate = useNavigate();
  const prefix = studentPrefix || "student";
  const firstName = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}")?.full_name?.split(" ")[0] || "Student";
    } catch {
      return "Student";
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [packs, setPacks] = useState([]);
  const [canStartTrial, setCanStartTrial] = useState(false);
  const [buyingId, setBuyingId] = useState(null);
  const { requestPurchase, purchaseDialog } = useConfirmPurchase();

  const load = useCallback(async () => {
    const [mocks, papers, access, bundles] = await Promise.allSettled([
      api.get(`${prefix}/mock-exam/purchases`),
      api.get(`${prefix}/paper/my-purchases`),
      api.get(`${prefix}/subscription/access`),
      api.get("/bundles"),
    ]);
    const data = (r) => (r.status === "fulfilled" ? r.value?.data?.data : null);
    setItems(toItems(data(mocks), data(papers), prefix));
    setCanStartTrial(data(access)?.can_start_free_trial === true);
    setPacks((data(bundles) || []).filter((p) => !p.already_owned && !p.covered_by_plan));
    setLoading(false);
  }, [prefix]);

  useEffect(() => {
    let cancelled = false;
    // A pack bought here returns to this page; record it before listing what they own.
    completePanelPurchase()
      .then((outcome) => {
        if (cancelled || !outcome) return;
        const message = PANEL_PURCHASE_MESSAGES[outcome];
        if (outcome === "cancelled") toast.info(message);
        else if (message) toast.success(message);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) load();
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const buyPack = (pack) => {
    setBuyingId(pack.id);
    requestPurchase("bundle", pack.id, {
      name: pack.name,
      price: pack.price_display || null,
      onCancel: () => setBuyingId(null),
    });
  };
  const seePlans = () => navigate(`/${prefix}/choose-plan?plans=1`);
  const planLine = `Mock exams, practice papers, worksheets, flash cards and games.${
    canStartTrial ? " Start with a 7-day free trial." : ""
  }`;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <CircularProgress thickness={5} size={56} sx={{ color: "#764ba2" }} />
      </Box>
    );
  }

  const hasItems = items.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 4 }}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: "#111827", mb: 1, fontSize: { xs: "1.5rem", sm: "2.125rem" } }}>
          Welcome back, {firstName}!
        </Typography>
        <Typography sx={{ color: "#374151", fontSize: { xs: "1rem", md: "1.05rem" } }}>
          {hasItems ? "Pick up where you left off." : "You don't have a plan yet. Choose how you'd like to practise."}
        </Typography>
      </Box>

      <GettingStartedCard role="student" />

      {hasItems ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ p: 2, border: "1px solid #DDD6FE", borderRadius: "16px", bgcolor: "#F5F3FF" }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: "#EDE9FE", color: PURPLE, display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <PhoneIcon fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>No plan · you're buying papers one at a time</Typography>
              <Typography variant="body2" sx={{ color: "#4B5563" }}>
                A plan opens every mock exam and practice paper, plus worksheets, flash cards and games. What you bought stays yours.
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" onClick={seePlans} sx={{ ...primaryBtnSx, whiteSpace: "nowrap" }}>
            See plans
          </Button>
        </Stack>
      ) : null}

      {hasItems ? (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <SectionHeading>Your mock exams and papers</SectionHeading>
            {items.length > LIST_LIMIT ? (
              <Button component={RouterLink} to={`/${prefix}/mock-exams?tab=exam`} sx={{ textTransform: "none", fontWeight: 700, color: PURPLE }}>
                View all ({items.length})
              </Button>
            ) : null}
          </Stack>
          <Box sx={{ ...cardSx, p: 0, overflow: "hidden" }}>
            {items.slice(0, LIST_LIMIT).map((item, i) => {
              const chip = STATUS_CHIP[item.status];
              const done = item.status === "Completed";
              return (
                <Stack
                  key={item.key}
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 1, sm: 2 }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  sx={{ px: { xs: 2, md: 2.75 }, py: 2, borderTop: i ? "1px solid #EEF0F6" : "none" }}
                >
                  <Chip
                    size="small"
                    label={item.kind}
                    sx={{
                      fontWeight: 700,
                      bgcolor: item.kind === "Mock exam" ? "#FEE2E2" : "#DCFCE7",
                      color: item.kind === "Mock exam" ? "#991B1B" : "#14532D",
                    }}
                  />
                  <Typography sx={{ flex: 1, fontWeight: 700, minWidth: 0 }}>{item.name}</Typography>
                  <Chip size="small" label={item.status} sx={{ fontWeight: 600, bgcolor: chip.bg, color: chip.fg }} />
                  <Button
                    component={RouterLink}
                    to={item.to}
                    variant={done ? "outlined" : "contained"}
                    sx={
                      done
                        ? { ...outlineBtnSx, minWidth: 116, color: PURPLE, borderColor: PURPLE }
                        : { ...primaryBtnSx, minHeight: 40, minWidth: 116, px: 2 }
                    }
                  >
                    {item.action}
                  </Button>
                </Stack>
              );
            })}
          </Box>

          <SectionHeading>Get more practice</SectionHeading>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
            <Box sx={{ ...cardSx, p: 2.5 }}>
              <PackRows packs={packs} buyingId={buyingId} onBuy={buyPack} />
              {!packs.length ? (
                <Typography variant="body2" sx={{ color: "#4B5563" }}>You own both packs.</Typography>
              ) : null}
            </Box>
            <Box sx={{ ...cardSx, p: 2.5, display: "flex", flexDirection: "column", gap: 1.5, justifyContent: "center" }}>
              <Typography variant="body2" sx={{ color: "#4B5563" }}>Or buy single papers, from £4.99.</Typography>
              <SingleLinks prefix={prefix} />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <SectionHeading>How would you like to practise?</SectionHeading>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 2.5 }}>
            <Box sx={{ ...cardSx, border: `2px solid ${PURPLE}`, boxShadow: "0 6px 24px rgba(91,63,214,0.10)", display: "flex", flexDirection: "column", gap: 1.75 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: "#EDE9FE", color: PURPLE, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <PhoneIcon />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: PURPLE }}>
                  11 Plus Magic plan
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800, lineHeight: 1.3 }}>
                Get every mock exam and practice paper in one plan
              </Typography>
              <Typography sx={{ color: "#4B5563", lineHeight: 1.55 }}>{planLine}</Typography>
              <Stack spacing={1} sx={{ display: { xs: "none", sm: "flex" } }}>
                {["Every mock exam and practice paper", "Worksheets marked online or from a scan", "Use it on the app and on this portal"].map((text) => (
                  <Stack key={text} direction="row" spacing={1.25} alignItems="center">
                    <CheckIcon sx={{ color: "#16A34A", fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: "#374151" }}>{text}</Typography>
                  </Stack>
                ))}
              </Stack>
              <Box sx={{ mt: "auto", pt: 0.5 }}>
                <Button variant="contained" onClick={seePlans} sx={{ ...primaryBtnSx, width: { xs: "100%", sm: "auto" } }}>
                  See plans
                </Button>
              </Box>
            </Box>

            <Box sx={{ ...cardSx, display: "flex", flexDirection: "column", gap: 1.75 }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: "#F3E8FF", color: "#7C3AED", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <CartIcon />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#7C3AED" }}>
                  Single papers · no plan needed
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800 }}>Buy only what you need</Typography>
              <PackRows packs={packs} buyingId={buyingId} onBuy={buyPack} />
              <Typography variant="body2" sx={{ color: "#4B5563" }}>
                Or buy single papers, from £4.99. No need to leave the portal.
              </Typography>
              <Box sx={{ mt: "auto" }}>
                <SingleLinks prefix={prefix} />
              </Box>
            </Box>
          </Box>

          <SectionHeading>Your mock exams and papers</SectionHeading>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems={{ xs: "flex-start", sm: "center" }} sx={cardSx}>
            <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "#ECFDF5", color: "#047857", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <PaperIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 17 }}>Nothing bought yet</Typography>
              <Typography variant="body2" sx={{ color: "#4B5563" }}>
                Mock exams and practice papers you buy appear here, ready to start.
              </Typography>
            </Box>
            <Button component={RouterLink} to={`/${prefix}/mock-exams?tab=exam`} sx={{ textTransform: "none", fontWeight: 700, color: PURPLE }}>
              Browse mock exams
            </Button>
          </Stack>
        </>
      )}
      {purchaseDialog}
    </Box>
  );
}
